import { useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { addBreadcrumb } from '@sentry/react';

import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
import { PrimaryInternalFuelingStatus } from '@ocx-app/modules/transaction/types';
import { useCreateLoyaltyEvent } from '@ocx-app/hooks/useCreateLoyaltyEvent';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useMembership } from '@ocx/data-membership';
import { useAppsFlyer } from '@ocx/cap-appsflyer';

import { useWatchTransaction } from '../../receipt/hooks/use-watch-transaction.hook';
import { useMapTransactionStatuses } from './use-map-transaction-statuses.hook';

export type UseFuelingStatusControllerReturns = {
  transactionId: string;
  loading: boolean;
  isTransactionStatusTimedOut: boolean;
  status: PrimaryInternalFuelingStatus | null;
  pumpNumber: string | null;
};

export type UseFuelingStatusControllerParams = {
  onError(error?: ApolloError): void;
};

export const useFuelingStatusController = ({
  onError,
}: UseFuelingStatusControllerParams): UseFuelingStatusControllerReturns => {
  const { triggerEvent } = useRudderStack();
  const { logEvent: logAppsFlyerEvent } = useAppsFlyer();
  const { transactionId = '' } = useParams<{ transactionId: string }>();
  const { membership } = useMembership();
  const [createLoyaltyEvent] = useCreateLoyaltyEvent({
    membershipId: membership.id,
  });

  const onPumpAuthorized = useCallback(() => {
    // TODO: `type` should be payment method type, will be addressed in OCX-536
    triggerEvent('pay_at_pump_authorized', { type: '' });

    createLoyaltyEvent('pay_at_pump_authorized').catch((error) => {
      addBreadcrumb({
        message: 'Create pay at pump loyalty event failed.',
        data: { membershipId: membership.id, error },
      });
    });
  }, [createLoyaltyEvent, membership.id, triggerEvent]);

  const onTransactionCompleted = useCallback(() => {
    triggerEvent('pay_at_pump_fueling_completed');
    logAppsFlyerEvent({
      eventName: 'af_purchase',
      eventValue: { af_receipt_id: transactionId },
    });
  }, [triggerEvent, transactionId, logAppsFlyerEvent]);

  const { loading, transaction, isTransactionStatusTimedOut } = useWatchTransaction({
    transactionId,
    onError,
    onTransactionCompleted,
    onPumpAuthorized,
  });
  const transactionStatuses = useMapTransactionStatuses({ transaction });

  return {
    transactionId,
    loading,
    isTransactionStatusTimedOut,
    status: transactionStatuses.primaryFuelingStatus,
    pumpNumber: transaction?.fuelingPosition ?? null,
  };
};
